import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import styles from './index.module.scss';
import { logoutUser, addNotificationToken, fcmAdded } from 'reducers/auth';
import {
  getGeneralInfo,
  getUserRoles,
  getUserSelectedLanguage,
} from 'reducers/users';
import { getCountryCodes } from 'reducers/utils';
import {
  getCurrentPage,
  updatePage,
  openDetails,
  getError,
  removeError,
  showSnackbar,
} from 'reducers/ui';

import { PAGE_URLS } from 'utils/constants';

import { updateURLQueryString } from 'sagas/user/help';
import i18next from 'i18next';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import cn from 'classnames';
import { useMediaQuery } from '@material-ui/core';
import Header from 'containers/DashboardLayout/Header/Header';
import Sidebar from 'containers/DashboardLayout/Sidebar/Sidebar';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import IconProducts from '@material-ui/icons/LocalMall';
import ServiceWorkerWrapper from 'serviceWorkerWrapper';
import { fetchGlobalList, fetchGlobalUsers } from 'reducers/globalList';
import {
  getSecurityCompanyFeatures,
  getSecurityCompanyServices,
  getSitesEnabled,
} from 'reducers/securityCompany';

import UserProfile from 'containers/UserProfile';
import { messageActions } from 'containers/Pages/Messages';
import { actions as cleanerTaskActions } from 'containers/Pages/Tasks/CleanerTasks/Modules/dataGridTable';
import { actions as templateTaskActions } from 'containers/Pages/Tasks/Template/Modules/dataGridTable';
import { zoneActions } from 'containers/Pages/Locations/Zones';
import { actions as businessClientActions } from 'containers/Pages/People/BusinessClients/Modules/dataGridTable';
import { actions as customerActions } from 'containers/Pages/People/Customers/Modules/dataGridTable';
import { visitorActions } from 'containers/Pages/Locations/Visitors';
import { actions as assetTypeActions } from 'containers/Pages/Assets/AssetTypes/Modules/dataGridTable';
import { actions as departmentTableActions } from 'containers/Pages/People/Departments/Modules/dataGridTable';
import { assetGridActions } from 'containers/Pages/Assets/Assets';
import { personnelGridActions } from 'containers/Pages/People/Personnel';
import { siteDataGridActions } from 'containers/Pages/Locations/Sites';
import { eventsGridActions } from 'containers/Pages/Events';
import {
  emergencyDataGridAction,
  emergencySelectors,
} from 'containers/Pages/Tasks/Emergencies';
import { actions as patrolTemplateActions } from 'containers/Pages/Tasks/PatrolTemplates/Modules/dataGridTable';
import { tripActions } from 'containers/Pages/Tasks/Trips';
import { getAccessibleNavigationMenu } from 'utils/navigation';
import { useHistory } from 'react-router-dom';
import { useBroadcastChannel } from './useBroadcastChannel';
import { DISTRESS_RESPONSE_STATUS } from 'utils/constants/distressStatus';
import { attendanceActions } from 'containers/Pages/People/Attendance';
import { patrolDataGridActions } from 'containers/Pages/Tasks/Patrols';
import { taskTypeActions } from 'containers/Pages/Tasks/TaskTypes';
import { workOrderGridTableActions } from 'containers/Pages/Tasks/WorkOrders';
import { securityGridTableActions } from 'containers/Pages/Tasks/Security';
import {
  AdminPanelIcon,
  AssetsIcon,
  CalendarIcon_New,
  DashboardIcon,
  EventsNotificationIcon,
  MapIcon,
  MessagesIcon,
  PeopleBadgeIcon,
  TaskAssignmentIcon,
} from 'Icons';
import { visitorTypeActions } from 'containers/Pages/Locations/VisitorTypes';
import { eventTypeActions } from 'containers/Pages/Admin/EventTypes';
import SplitPane from 'react-split-pane';
import {
  PANEL_SPLIT,
  PANEL_SPLIT_SIZE,
  PAGE_PANEL_SPLIT_SIZE,
} from 'utils/constants/index';
import Map from 'containers/Base/Map';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import { TASK_TYPE } from 'utils/constants/task';

const Dashboard = ({
  distress,
  openDetails,
  currentPage,
  generalInfo,
  services,
  addNotificationToken,
  setDistressDetails,
  fetchDistressDetails,
  fetchAssetList,
  fcmAvailable,
  roles,
  setRefreshDistresses,
  setRefreshEvents,
  personnelFilters,
  managerFilters,
  assetFilters,
  cleanerTaskDetail,
  showSnackbar,
  dispatchSetRefreshSite,
  dispatchSetRefreshVisitorType,
  fetchGlobalDepartmentList,
  selectedLanguage,
  features,
  sitesEnabled,
  //v2 refresh functions
  refreshCleanerTaskPage,
  refreshTemplateTaskPage,
  refreshZones,
  refreshBusinessClients,
  refreshCustomers,
  refreshAssetType,
  refreshDepartments,
  refreshTemplatePatrolPage,
  refreshAttendanceTracking,
  refreshVisitors,
  refreshTaskTypes,
  refreshMessageList,
  // eslint-disable-next-line react/prop-types
  children,
  fetchGlobalUsers,
  logout,
  refreshPersonnel,
  refreshTrips,
  refreshEmergencies,
  refreshPatrolTasks,
  refreshWorkOrderTasks,
  refreshSecurityTasks,
  setRefreshEventTypes,
  fetchTripDetails,
  fetchPersonelList,
  fetchSitesList,
  refreshEventList,
  fetchCleanerList,
  fetchWorkorderList,
  fetchSecurityList,
}) => {
  const [viewProfile, setViewProfile] = useState(false);
  const [notificationDisabled, setNotificationDisabled] = useState(false);
  const [openMap, setOpenMap] = useState(true);
  const history = useHistory();

  const classes = useStyles();
  // check if the browser is safari
  const isSafari = window.safari;

  useBroadcastChannel('sw-messages', isSafari, ev => redirectionMessage(ev));
  useBroadcastChannel('other-channel', isSafari, ev => refreshData(ev));

  const checkIfNotificationAllowed = useCallback(() => {
    // dont call
    if (fcmAvailable) {
      return;
    }
    if (!isEmpty(Notification) && Notification?.permission !== 'granted') {
      // ios cannot read Notification variable
      setNotificationDisabled(true);
    } else {
      const token = window.sessionStorage.getItem('fcm_token');
      addNotificationToken(generalInfo.regionId, token);
    }
  }, [addNotificationToken, generalInfo, fcmAvailable]);

  const redirectionMessage = useCallback(
    event => {
      // CASE 1: NEW -> refresh the list
      if (event.data.type === 'NEW') {
        history.push(PAGE_URLS.EMERGENCIES.link);
        refreshEmergencies(true);
        updateURLQueryString(PAGE_URLS.EMERGENCIES.link);
        // open the distress panel and fetch the details
        openDetails(PAGE_URLS.EMERGENCIES.link);
        fetchDistressDetails(event.data.oid);
      } else if (
        event.data.type === DISTRESS_RESPONSE_STATUS.ABORTED.id ||
        event.data.type === DISTRESS_RESPONSE_STATUS.RESOLVED.id ||
        event.data.type === 'STATUS_CHANGED'
      ) {
        refreshEmergencies(true);
      } else if (!isEmpty(distress) && event.data.oid === distress.id.value) {
        // CASE 2: NOT NEW -> SOMETHING OPENED
        fetchDistressDetails(event.data.oid);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      distress,
      fetchDistressDetails,
      setDistressDetails,
      openDetails,
      setRefreshDistresses,
    ],
  );
  const refreshData = useCallback(
    event => {
      const { svc, type } = event.data;
      if (event.data?.body) {
        showSnackbar({
          message: `${event.data.title} - ${event.data.body}`,
          typeOfSnackbar: 'success',
        });
      }
      if (event.data?.notification) {
        showSnackbar({
          message: `${event.data.notification.title} - ${event.data.notification.body}`,
          typeOfSnackbar: 'success',
        });
        refreshEmergencies(true);
      }

      switch (svc) {
        case 'REGION':
          if (type.includes('ZONE')) {
            refreshZones(true);
          }
          if (type.includes('SITE')) {
            dispatchSetRefreshSite(true);
            dispatchSetRefreshVisitorType(true);
            fetchSitesList({ filters: {}, from: 0 });
          }
          break;
        case 'USERS':
          if (type.includes('BUS_CLIENT')) {
            refreshBusinessClients(true);
            break;
          }

          if (type.includes('USER')) {
            // refresh and call global user list
            fetchGlobalUsers();
            refreshCustomers(true);
            refreshPersonnel(true);
            fetchPersonelList({
              filters: {
                role: [
                  'CLEANER',
                  'DRIVER',
                  'GUARD',
                  'MANAGER',
                  'RESPONDER',
                  'SUPERVISOR',
                ],
              },
              from: 0,
            });
          }
          if (type.includes('STATUS')) {
            refreshCustomers(true);
            refreshPersonnel(true);
            refreshAttendanceTracking(true);
            fetchPersonelList({
              filters: {
                role: [
                  'CLEANER',
                  'DRIVER',
                  'GUARD',
                  'MANAGER',
                  'RESPONDER',
                  'SUPERVISOR',
                ],
              },
              from: 0,
            });
          }
          if (type.includes('VISITOR')) {
            refreshVisitors(true);
            dispatchSetRefreshSite(true);
            break;
          }
          if (type.includes('DEPARTMENT')) {
            fetchGlobalDepartmentList({ category: 'departments' });

            refreshDepartments(true);
            break;
          }

          break;
        case 'EVENTS':
          setRefreshEvents(true);
          setRefreshEventTypes(true);
          refreshEventList({ filters: {}, from: 0 });
          if (type === 'DIRECT_MESSAGE') {
            refreshMessageList(true);
          }
          break;

        case 'TRANSPORT':
          refreshTrips(true);
          fetchTripDetails(event?.data?.oid);
          break;
        case 'ASSETS':
          // fire message to the allocated user

          if (type.includes('TYPE')) {
            refreshAssetType(true);
            break;
          }
          if (type.includes('ASSET')) {
            fetchAssetList(true);
            break;
          }
          break;
        case 'PATROL':
          if (type === 'DIRECT_MESSAGE') {
            refreshMessageList(true);
          }

          if (type.includes('PATROL')) {
            refreshPatrolTasks(true);
            refreshTemplatePatrolPage(true);
          }
          break;
        case 'DISTRESS':
          if (!isEmpty(distress)) {
            fetchDistressDetails(distress.id);
          }
          refreshEmergencies(true);
          break;
        case 'TASK':
          if (type === 'DIRECT_MESSAGE') {
            refreshMessageList(true);
          }
          if (type.includes('TASK_TYPE_UPDATED')) {
            refreshTaskTypes(true);
          }
          if (type.includes('TASK')) {
            refreshCleanerTaskPage(true);
            refreshWorkOrderTasks(true);
            refreshSecurityTasks(true);
            fetchCleanerList({
              from: 0,
              filters: { taskCategory: TASK_TYPE.CLEANING.id },
            });
            fetchWorkorderList({
              from: 0,
              filters: { taskCategory: TASK_TYPE.MAINTENANCE.id },
            });
            fetchSecurityList({
              from: 0,
              filters: { taskCategory: TASK_TYPE.SECURITY.id },
            });
            refreshTemplateTaskPage(true);
          }
          break;
        default:
          break;
      }
      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      showSnackbar,
      fetchAssetList,
      fetchDistressDetails,
      setRefreshEvents,
      setRefreshDistresses,
      distress,
      cleanerTaskDetail,
      currentPage,
      personnelFilters,
      managerFilters,
      assetFilters,
      fetchGlobalDepartmentList,
      refreshDepartments,
      fetchGlobalUsers,
      setRefreshEventTypes,
    ],
  );

  useEffect(() => {
    checkIfNotificationAllowed();
  }, [checkIfNotificationAllowed]);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const isMobile = !isDesktop;

  const [isSidebarOpenMobile, setIsSidebarOpenMobile] = React.useState(false);
  const [
    isSidebarCollapsedDesktop,
    setIsSidebarCollapsedDesktop,
  ] = React.useState(false);

  function handleSidebarMobileToggle() {
    setIsSidebarOpenMobile(!isSidebarOpenMobile);
  }

  function handleSidebarToggle() {
    // Open/close on mobile
    if (isMobile) {
      setIsSidebarOpenMobile(!isSidebarOpenMobile);
    } else {
      // Collapse/un-collapse on desktop
      setIsSidebarCollapsedDesktop(!isSidebarCollapsedDesktop);
    }
  }
  useEffect(() => {
    i18next.changeLanguage(selectedLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add the sidebar menu items here
  const menuMain = [
    {
      ...PAGE_URLS.DASHBOARD,
      Icon: DashboardIcon,
    },
    {
      ...PAGE_URLS.EVENTS,
      Icon: EventsNotificationIcon,
    },
    {
      ...PAGE_URLS.MESSAGES,
      Icon: MessagesIcon,
    },
    {
      subMenu: 'Tasks',
      Icon: TaskAssignmentIcon,
      items: [
        PAGE_URLS.TASK_TYPES,
        PAGE_URLS.TRIP_TYPES,
        PAGE_URLS.TEMPLATE_TASKS,
        PAGE_URLS.TEMPLATE_PATROLS,
        PAGE_URLS.CLEANING_TASKS,
        PAGE_URLS.WORK_ORDERS,
        PAGE_URLS.SECURITY_TASKS,
        PAGE_URLS.PATROLS,
        PAGE_URLS.TRIPS,
        PAGE_URLS.EMERGENCIES,
      ],
    },
    {
      subMenu: 'People',
      Icon: PeopleBadgeIcon,
      items: [
        PAGE_URLS.PERSONNEL,
        PAGE_URLS.CUSTOMERS,
        PAGE_URLS.BUSINESS_CLIENTS,
        PAGE_URLS.DEPARTMENTS,
        PAGE_URLS.ATTENDANCE,
      ],
    },
    {
      subMenu: 'Assets',
      Icon: AssetsIcon,
      items: [PAGE_URLS.ASSETS, PAGE_URLS.ASSET_TYPES],
    },
    {
      subMenu: 'Locations',
      Icon: MapIcon,
      items: [
        PAGE_URLS.ZONES,
        PAGE_URLS.SITES,
        PAGE_URLS.VISITORS,
        PAGE_URLS.VISITOR_TYPES,
      ],
    },
    {
      subMenu: 'Admin',
      Icon: AdminPanelIcon,
      items: [PAGE_URLS.EVENT_TYPES, PAGE_URLS.ACCOUNT],
    },
    {
      subMenu: 'Regions',
      Icon: IconProducts,
      items: [PAGE_URLS.REGION, PAGE_URLS.COMPANY, PAGE_URLS.PENDING_COMPANY],
    },

    {
      ...PAGE_URLS.CALENDAR,
      Icon: CalendarIcon_New,
    },
  ];

  return (
    <>
      <div className={classes.dashboardContainer}>
        <div
          className={cn(
            classes.headerContainer,
            { [classes.headerContainerDesktop]: isDesktop },
            {
              [classes.headerContainerDesktopDrawerCollapsed]:
                isDesktop && isSidebarCollapsedDesktop,
            },
          )}
        >
          <Header
            onToggle={handleSidebarToggle}
            setViewProfile={setViewProfile}
            isSidebarCollapsedDesktop={isSidebarCollapsedDesktop}
            isMobile={isMobile}
          />
        </div>
        <div
          className={cn(
            classes.sidebarContainer,
            { [classes.sidebarContainerMobile]: isMobile },
            { [classes.sidebarContainerDesktop]: isDesktop },
            {
              [classes.sidebarContainerDesktopDrawerCollapsed]:
                isDesktop && isSidebarCollapsedDesktop,
            },
          )}
        >
          <Hidden mdUp implementation="css">
            <Drawer
              variant="temporary"
              anchor={theme.direction === 'rtl' ? 'right' : 'left'}
              open={isSidebarOpenMobile}
              onClose={handleSidebarMobileToggle}
              classes={{
                paper: cn(classes.drawer, classes.drawerMobile),
              }}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
            >
              <Sidebar
                isDesktop={isDesktop}
                isMobile={isMobile}
                isSidebarCollapsedDesktop={isSidebarCollapsedDesktop}
                isSidebarOpenMobile={isSidebarOpenMobile}
                menuMain={getAccessibleNavigationMenu({
                  menuItems: menuMain,
                  services,
                  features,
                  roles,
                  sitesEnabled,
                })}
              />
            </Drawer>
          </Hidden>
          <Hidden smDown implementation="css">
            <Drawer
              classes={{
                paper: cn(classes.drawer, classes.drawerDesktop, {
                  [classes.drawerDesktopCollapsed]: isSidebarCollapsedDesktop,
                }),
                root: classes.drawerOverRide,
              }}
              variant="permanent"
            >
              <Sidebar
                isDesktop={isDesktop}
                isMobile={isMobile}
                isSidebarCollapsedDesktop={isSidebarCollapsedDesktop}
                isSidebarOpenMobile={isSidebarOpenMobile}
                menuMain={getAccessibleNavigationMenu({
                  menuItems: menuMain,
                  services,
                  features,
                  roles,
                  sitesEnabled,
                })}
              />
            </Drawer>
          </Hidden>
        </div>
        <main className={classes.content}>
          {viewProfile && (
            <UserProfile
              cancel={() => setViewProfile(!viewProfile)}
              logout={logout}
            />
          )}
          {<ServiceWorkerWrapper />}
          <div className={classes.headerSpacer} />
          <SplitPane
            split={PANEL_SPLIT.VERTICAL}
            size={
              openMap
                ? PAGE_PANEL_SPLIT_SIZE[history.location.pathname] ||
                  PANEL_SPLIT_SIZE.VERTICAL_DEFAULT
                : PANEL_SPLIT_SIZE.VERTICAL_CLOSED
            }
            minSize={200}
            allowResize={true}

            // defaultSize="calc(100vh - 88px)"
          >
            {children}
            <div className={classes.mapContainer}>
              <div
                onClick={() => setOpenMap(!openMap)}
                className={classes.shiftIcon}
              >
                {openMap ? (
                  <ArrowRightIcon
                    classes={{ root: classes.mySvgStyle }}
                    fontSize="large"
                  />
                ) : (
                  <ArrowLeftIcon
                    fontSize="large"
                    classes={{ root: classes.mySvgStyle }}
                  />
                )}
              </div>
              <Map />
            </div>
          </SplitPane>
          {/* </Paper> */}
        </main>
      </div>
      {/* {error && <Error onClick={resetError} error={error} />} */}
      {notificationDisabled && (
        <div className={styles.notificationError}>
          Please enable notification to receive live updates!
        </div>
      )}
    </>
  );
};

Dashboard.propTypes = {
  userRoles: PropTypes.array,
  openDetails: PropTypes.func,
  refreshTemplateTaskPage: PropTypes.func,
  updatePage: PropTypes.func,
  currentPage: PropTypes.string,
  distress: PropTypes.object,
  distresses: PropTypes.array,
  services: PropTypes.array,
  countryCodes: PropTypes.object.isRequired,
  generalInfo: PropTypes.object.isRequired,
  logout: PropTypes.func.isRequired,
  history: PropTypes.object,
  addNotificationToken: PropTypes.func,
  fetchDistressDetails: PropTypes.func.isRequired,
  setDistressDetails: PropTypes.func.isRequired,
  fcmAvailable: PropTypes.bool,
  includeTransport: PropTypes.bool,
  error: PropTypes.string,
  resetError: PropTypes.func,
  fetchAssetList: PropTypes.func,
  setRefreshDistresses: PropTypes.func,
  setRefreshEvents: PropTypes.func,
  personnelFilters: PropTypes.object,
  managerFilters: PropTypes.object,
  assetFilters: PropTypes.func,
  cleanerTaskDetail: PropTypes.object,
  showSnackbar: PropTypes.func,
  roles: PropTypes.array,
  features: PropTypes.array,
  eventFilters: PropTypes.object,
  dispatchSetRefreshSite: PropTypes.func,
  dispatchSetRefreshVisitorType: PropTypes.func,
  //portal v2 refresh
  refreshCustomers: PropTypes.func,
  refreshVisitors: PropTypes.func,
  refreshAssetType: PropTypes.func,
  refreshDepartments: PropTypes.func,
  refreshCleanerTaskPage: PropTypes.func,
  refreshTaskTypes: PropTypes.func,
  refreshZones: PropTypes.func,
  refreshBusinessClients: PropTypes.func,
  refreshEmergencies: PropTypes.func,
  refreshTemplatePatrolPage: PropTypes.func,
  refreshPersonnel: PropTypes.func,
  refreshAttendanceTracking: PropTypes.func,
  refreshTrips: PropTypes.func,
  refreshMessageList: PropTypes.func,
  //global
  fetchGlobalDepartmentList: PropTypes.func,
  fetchGlobalUsers: PropTypes.func,
  selectedLanguage: PropTypes.string,
  sitesEnabled: PropTypes.bool,
  refreshPatrolTasks: PropTypes.func,
  refreshWorkOrderTasks: PropTypes.func,
  refreshSecurityTasks: PropTypes.func,
  setRefreshEventTypes: PropTypes.func,
  fetchTripDetails: PropTypes.func,
  fetchPersonelList: PropTypes.func,
  fetchSitesList: PropTypes.func,
  refreshEventList: PropTypes.func,
  fetchCleanerList: PropTypes.func,
  fetchWorkorderList: PropTypes.func,
  fetchSecurityList: PropTypes.func,
};

const mapStateToProps = state => ({
  userRoles: getUserRoles(state),
  distress: emergencySelectors.getEmergencyDetailRaw(state),
  currentPage: getCurrentPage(state),
  countryCodes: getCountryCodes(state),
  generalInfo: getGeneralInfo(state),
  fcmAvailable: fcmAdded(state),
  error: getError(state),
  selectedLanguage: getUserSelectedLanguage(state),
  services: getSecurityCompanyServices(state),
  features: getSecurityCompanyFeatures(state),
  roles: getUserRoles(state),
  sitesEnabled: getSitesEnabled(state),
});

const mapDispatchToProps = dispatch => ({
  openDetails: location => dispatch(openDetails(location)),
  fetchDistressDetails: id =>
    dispatch(emergencyDataGridAction.setSelectedRowID(id)),
  setDistressDetails: () =>
    dispatch(emergencyDataGridAction.setGridRowDetails({ details: {} })),
  updatePage: location => dispatch(updatePage(location)),
  logout: () => dispatch(logoutUser()),
  fetchAssetList: key => dispatch(assetGridActions.setForceRefresh(key)),
  addNotificationToken: (regionId, token) =>
    dispatch(addNotificationToken(regionId, token)),
  resetError: () => dispatch(removeError()),
  showSnackbar: ({ message, typeOfSnackbar }) =>
    dispatch(showSnackbar({ message, typeOfSnackbar })),
  fetchGlobalDepartmentList: ({ category }) =>
    dispatch(fetchGlobalList({ category })),
  fetchGlobalUsers: () => dispatch(fetchGlobalUsers()),
  //version 2 refresh methods
  refreshMessageList: val => dispatch(messageActions.setForceRefresh(val)),
  setRefreshEvents: val => dispatch(eventsGridActions.setForceRefresh(val)),
  refreshEventList: val => dispatch(eventsGridActions.fetchGridList(val)),
  setRefreshEventTypes: val => dispatch(eventTypeActions.setForceRefresh(val)),
  refreshCleanerTaskPage: val =>
    dispatch(cleanerTaskActions.setForceRefresh(val)),
  refreshTemplateTaskPage: val =>
    dispatch(templateTaskActions.setForceRefresh(val)),
  refreshTemplatePatrolPage: val =>
    dispatch(patrolTemplateActions.setForceRefresh(val)),
  refreshZones: key => dispatch(zoneActions.setForceRefresh(key)),
  refreshCustomers: key => dispatch(customerActions.setForceRefresh(key)),
  refreshAttendanceTracking: key =>
    dispatch(attendanceActions.setForceRefresh(key)),
  refreshVisitors: key => dispatch(visitorActions.setForceRefresh(key)),
  refreshTaskTypes: key => dispatch(taskTypeActions.setForceRefresh(key)),
  refreshAssetType: key => dispatch(assetTypeActions.setForceRefresh(key)),
  refreshPersonnel: key => dispatch(personnelGridActions.setForceRefresh(key)),
  fetchPersonelList: obj => dispatch(personnelGridActions.fetchGridList(obj)),
  refreshDepartments: key =>
    dispatch(departmentTableActions.setForceRefresh(key)),
  refreshBusinessClients: key =>
    dispatch(businessClientActions.setForceRefresh(key)),
  refreshTrips: key => dispatch(tripActions.setForceRefresh(key)),
  fetchTripDetails: id => dispatch(tripActions.setSelectedRowID(id)),
  refreshEmergencies: key =>
    dispatch(emergencyDataGridAction.setForceRefresh(key)),
  dispatchSetRefreshSite: val =>
    dispatch(siteDataGridActions.setForceRefresh(val)),
  fetchSitesList: val => dispatch(siteDataGridActions.fetchGridList(val)),
  dispatchSetRefreshVisitorType: val =>
    dispatch(visitorTypeActions.setForceRefresh(val)),
  refreshPatrolTasks: val =>
    dispatch(patrolDataGridActions.setForceRefresh(val)),
  refreshWorkOrderTasks: val =>
    dispatch(workOrderGridTableActions.setForceRefresh(val)),
  refreshSecurityTasks: val =>
    dispatch(securityGridTableActions.setForceRefresh(val)),
  fetchCleanerList: val => dispatch(cleanerTaskActions.fetchGridList(val)),
  fetchWorkorderList: val =>
    dispatch(workOrderGridTableActions.fetchGridList(val)),
  fetchSecurityList: val =>
    dispatch(securityGridTableActions.fetchGridList(val)),
});

const useStyles = makeStyles(theme => ({
  dashboardContainer: {
    display: 'flex',
  },
  drawerOverRide: {
    '& [class*="MuiDrawer-paperAnchorDockedLeft"]': {
      borderRight: `1px solid ${theme?.sidebar?.border}`,
    },
  },
  headerContainer: {
    top: 0,
    left: 0,
    right: 0,
    position: 'absolute',
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  headerContainerDesktop: {
    left: 'auto',
    width: `calc(100% - ${theme?.sidebar?.width}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  headerContainerDesktopDrawerCollapsed: {
    width: `calc(100% - ${theme?.sidebar?.widthCollapsed}px)`,
  },
  sidebarContainer: {
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      width: theme?.sidebar?.width,
      flexShrink: 0,
    },
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  sidebarContainerMobile: {
    width: 0,
  },
  sidebarContainerDesktop: {
    width: theme?.sideba?.width,
  },
  sidebarContainerDesktopDrawerCollapsed: {
    [theme.breakpoints.up('md')]: {
      width: theme?.sidebar?.widthCollapsed,
    },
  },
  drawer: {},
  drawerMobile: {
    width: theme?.sidebar?.width,
  },
  drawerDesktop: {
    width: '100%',
    position: 'absolute',
  },
  drawerDesktopCollapsed: {
    overflowX: 'hidden',
  },
  headerSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    height: '100vh',
    overflow: 'auto',
    flexDirection: 'column',
    display: 'flex',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
  },
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
  },
  fixedHeight: {
    height: 240,
  },
  mapContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flex: 1,
    boxShadow: '-0.5px 1px 3px -1px rgba(0,0,0,0.75);',
    zIndex: 99999999,
  },
  shiftIcon: {
    position: 'absolute',
    zIndex: 10,
    top: '49%',
    left: '-10px',
    borderRadius: '25%',
    cursor: 'pointer',
    padding: 0,
    fontSize: '10px',
    background: theme.palette.primary.main,
    height: '2rem',
    width: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mySvgStyle: {
    cursor: 'pointer',
    color: theme.palette.secondary.light,
  },
}));
export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
